import { useCallback, useEffect, useRef, useState } from "react";
import {
  INFINITE_SCROLL_PREFETCH_REMAINING,
  isNearScrollBottom,
} from "../utils/infiniteScrollPaging";

/**
 * Triggers `onLoadMore` when the user scrolls within `prefetchRemaining` items of the end.
 * Attaches an IntersectionObserver to the prefetch-index product row and listens to scroll.
 */
export function useProductListInfiniteScroll({
  itemCount = 0,
  hasMore = false,
  isLoading = false,
  onLoadMore,
  prefetchRemaining = INFINITE_SCROLL_PREFETCH_REMAINING,
}) {
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  const onLoadMoreRef = useRef(onLoadMore);
  const prefetchNodeRef = useRef(null);
  const observerRef = useRef(null);
  const [prefetchNodeVersion, setPrefetchNodeVersion] = useState(0);

  hasMoreRef.current = hasMore;
  isLoadingRef.current = isLoading;
  onLoadMoreRef.current = onLoadMore;

  const prefetchIndex = Math.max(0, itemCount - prefetchRemaining);

  const tryLoadMore = useCallback(() => {
    if (!hasMoreRef.current || isLoadingRef.current) return;
    onLoadMoreRef.current?.();
  }, []);

  const bindPrefetchNode = useCallback((node) => {
    if (prefetchNodeRef.current === node) return;
    prefetchNodeRef.current = node;
    setPrefetchNodeVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const node = prefetchNodeRef.current;
    if (!node || !hasMore || itemCount <= 0) return undefined;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          tryLoadMore();
        }
      },
      { root: null, rootMargin: "300px 0px 600px 0px", threshold: 0 }
    );
    observerRef.current.observe(node);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [hasMore, isLoading, itemCount, prefetchIndex, prefetchNodeVersion, tryLoadMore]);

  useEffect(() => {
    if (!hasMore || itemCount <= 0) return undefined;

    const checkScroll = () => {
      const node = prefetchNodeRef.current;
      if (node) {
        const rect = node.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 400) {
          tryLoadMore();
          return;
        }
      }
      if (isNearScrollBottom(400)) {
        tryLoadMore();
      }
    };

    window.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll, { passive: true });
    const frame = window.requestAnimationFrame(checkScroll);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [hasMore, isLoading, itemCount, prefetchIndex, prefetchNodeVersion, tryLoadMore]);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    []
  );

  return { bindPrefetchNode, prefetchIndex };
}

export default useProductListInfiniteScroll;
